import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ProductVariant } from '../../interfaces/wheels';
import { WheelCartService, CartItem } from '../../services/wheel-cart.service';
import { WheelPricingService, PricingCalculation } from '../../services/wheel-pricing.service';

@Component({
  selector: 'app-wheel-search-by-size-selector',
  templateUrl: './wheel-search-by-size-selector.component.html',
  styleUrls: ['./wheel-search-by-size-selector.component.scss']
})
export class WheelSearchBySizeSelectorComponent implements OnInit, OnChanges {
  @Input() product: ProductVariant;
  @Input() vehicleWheels: ProductVariant[];
  @Input() checkfitment: boolean = false;
  @Output() onCartItemsChange = new EventEmitter<CartItem[]>();
  @Output() onPriceChange = new EventEmitter<number>();

  // Size selections
  frontWheelSize: string | number = '';
  rearWheelSize: string | number = '';
  
  // Quantities
  frontQty = 2;
  rearQty = 2;
  
  // Pricing
  frontPrice = 0;
  rearPrice = 0;
  totalPrice = 0;
  
  // Sale pricing
  frontSalePrice = 0;
  rearSalePrice = 0;
  showFrontSale = false;
  showRearSale = false;

  constructor(
    private wheelCartService: WheelCartService,
    private wheelPricingService: WheelPricingService
  ) { }

  ngOnInit(): void {
    console.log('🚀 WheelSearchBySizeSelectorComponent initialized');
    this.debugVehicleWheelsData();
    this.initializePricing();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product || changes.vehicleWheels) {
      console.log('🔄 Input changes detected:', {
        product_changed: !!changes.product,
        vehicleWheels_changed: !!changes.vehicleWheels,
        vehicleWheels_length: this.vehicleWheels?.length || 0
      });
      
      if (changes.vehicleWheels) {
        this.debugVehicleWheelsData();
      }
      
      this.initializePricing();
    }
  }

  /**
   * Debug function to understand vehicleWheels data structure
   */
  private debugVehicleWheelsData(): void {
    console.log('🔍 Debugging vehicleWheels data:', {
      vehicleWheels_exists: !!this.vehicleWheels,
      vehicleWheels_length: this.vehicleWheels?.length || 0,
      vehicleWheels_sample: this.vehicleWheels?.slice(0, 2) || [],
      pricing_fields_sample: this.vehicleWheels?.length > 0 ? {
        first_wheel: {
          id: this.vehicleWheels[0].id,
          sku: this.vehicleWheels[0].sku,
          uae_retail_price: this.vehicleWheels[0].uae_retail_price,
          price: this.vehicleWheels[0].price,
          sale_price: this.vehicleWheels[0].sale_price,
          us_retail_price: this.vehicleWheels[0].us_retail_price,
          available_price_fields: Object.keys(this.vehicleWheels[0]).filter(key => key.includes('price'))
        }
      } : null
    });
  }

  /**
   * Gets whether this product has rear wheel specifications
   */
  get hasRearWheels(): boolean {
    return !!(this.product?.rear_size || this.product?.rear_rim_diameter);
  }

  /**
   * Gets the available wheel options for selection
   */
  get availableWheels(): ProductVariant[] {
    const wheels = this.vehicleWheels || [];
    console.log('🔧 Available wheels for selection:', {
      total_count: wheels.length,
      wheels_with_pricing: wheels.filter(w => w.uae_retail_price || w.price).length,
      sample_pricing: wheels.slice(0, 3).map(w => ({
        id: w.id,
        sku: w.sku,
        uae_retail_price: w.uae_retail_price,
        price: w.price,
        sale_price: w.sale_price
      }))
    });
    return wheels;
  }  /**
   * Handles wheel size selection
   */
  onSizeSelect(event: any, type: 'front' | 'rear'): void {
    const wheelId = event.target.value;
    const wheel = this.availableWheels.find(w => w.id == wheelId);
    
    console.log(`🔧 Wheel ${type} size selected:`, {
      wheelId: wheelId,
      wheel_exists: !!wheel,
      wheel: wheel ? {
        id: wheel.id,
        sku: wheel.sku,
        size: wheel.size,
        pricing: {
          uae_retail_price: wheel.uae_retail_price,
          us_retail_price: wheel.us_retail_price,
          price: wheel.price,
          sale_price: wheel.sale_price
        }
      } : null,
      available_wheels_count: this.availableWheels.length,
      event_value: event.target.value
    });
    
    if (!wheel) {
      console.error(`❌ No wheel found for ${type} selection with ID:`, wheelId);
      return;
    }

    if (type === 'front') {
      this.frontWheelSize = wheelId;
      console.log('🔄 Updating front pricing...');
      this.updateFrontPricing();
    } else {
      this.rearWheelSize = wheelId;
      console.log('🔄 Updating rear pricing...');
      this.updateRearPricing();
    }

    console.log('🔄 Calculating total price...');
    this.calculateTotalPrice();
    
    console.log('🔄 Updating cart items...');
    this.updateCartItems();
    
    console.log('🔄 Triggering cart items update for customization refresh');
    
    // Log final state
    console.log('📊 Final pricing state:', {
      frontPrice: this.frontPrice,
      rearPrice: this.rearPrice,
      totalPrice: this.totalPrice,
      frontSalePrice: this.frontSalePrice,
      rearSalePrice: this.rearSalePrice,
      showFrontSale: this.showFrontSale,
      showRearSale: this.showRearSale
    });
  }

  /**
   * Adjusts quantity for front or rear wheels
   */
  adjustQuantity(type: 'front' | 'rear', delta: number): void {
    if (type === 'front') {
      this.frontQty = Math.max(1, Math.min(4, this.frontQty + delta));
      this.updateFrontPricing();
    } else {
      this.rearQty = Math.max(1, Math.min(4, this.rearQty + delta));
      this.updateRearPricing();
    }

    this.calculateTotalPrice();
    this.updateCartItems();
  }

  /**
   * Formats wheel display text for dropdown options
   */
  formatWheelOption(wheel: ProductVariant): string {
    const size = wheel.size || `${wheel.rim_diameter}x${wheel.rim_width}`;
    const offset = wheel.offset || 'N/A';
    const boltPattern = wheel.bolt_pattern || 'N/A';
    
    return `Size ${size}, Offset ${offset}, Bolt Pattern ${boltPattern}`;
  }

  /**
   * Initializes pricing based on product data
   */
  private initializePricing(): void {
    if (!this.product) return;

    this.updateFrontPricing();
    this.updateRearPricing();
    this.calculateTotalPrice();
  }  /**
   * Updates front wheel pricing
   */
  private updateFrontPricing(): void {
    if (!this.frontWheelSize) {
      console.log('⚠️ No front wheel selected, resetting pricing');
      this.frontPrice = 0;
      this.frontSalePrice = 0;
      this.showFrontSale = false;
      return;
    }
    
    // Find the selected wheel variant to get correct pricing
    const selectedWheel = this.availableWheels.find(w => w.id == this.frontWheelSize);
    
    if (!selectedWheel) {
      console.warn('🚨 Front wheel variant not found for ID:', {
        wheelId: this.frontWheelSize,
        availableWheelIds: this.availableWheels.map(w => w.id),
        availableWheelsCount: this.availableWheels.length
      });
      this.frontPrice = 0;
      this.frontSalePrice = 0;
      this.showFrontSale = false;
      return;
    }

    console.log('🔍 Front wheel data for pricing:', {
      wheelId: this.frontWheelSize,
      selectedWheel: {
        id: selectedWheel.id,
        sku: selectedWheel.sku,
        uae_retail_price: selectedWheel.uae_retail_price,
        price: selectedWheel.price,
        sale_price: selectedWheel.sale_price,
        us_retail_price: selectedWheel.us_retail_price
      },
      all_pricing_fields: Object.keys(selectedWheel).filter(key => key.toLowerCase().includes('price')),
      pricing_values: Object.entries(selectedWheel)
        .filter(([key, value]) => key.toLowerCase().includes('price'))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    });

    // Try multiple pricing fields in order of preference
    const unitPrice = selectedWheel.uae_retail_price || 
                     selectedWheel.us_retail_price || 
                     selectedWheel.price || 
                     0;
                     
    console.log('💵 Unit price calculation:', {
      uae_retail_price: selectedWheel.uae_retail_price,
      us_retail_price: selectedWheel.us_retail_price,
      price: selectedWheel.price,
      final_unit_price: unitPrice
    });

    this.frontPrice = unitPrice * this.frontQty;

    // Handle sale pricing
    if (selectedWheel.sale_price && selectedWheel.sale_price > 0) {
      this.showFrontSale = true;
      this.frontSalePrice = selectedWheel.sale_price * this.frontQty;
    } else {
      this.showFrontSale = false;
      this.frontSalePrice = 0;
    }

    console.log('💰 Front pricing updated:', {
      wheelId: this.frontWheelSize,
      unitPrice,
      quantity: this.frontQty,
      totalPrice: this.frontPrice,
      salePrice: this.frontSalePrice,
      showSale: this.showFrontSale,
      wheelSku: selectedWheel.sku
    });
  }  /**
   * Updates rear wheel pricing
   */
  private updateRearPricing(): void {
    if (!this.rearWheelSize) {
      console.log('⚠️ No rear wheel selected, resetting pricing');
      this.rearPrice = 0;
      this.rearSalePrice = 0;
      this.showRearSale = false;
      return;
    }

    // Find the selected rear wheel variant to get correct pricing
    const selectedRearWheel = this.availableWheels.find(w => w.id == this.rearWheelSize);
    
    if (!selectedRearWheel) {
      console.warn('🚨 Rear wheel variant not found for ID:', {
        wheelId: this.rearWheelSize,
        availableWheelIds: this.availableWheels.map(w => w.id),
        availableWheelsCount: this.availableWheels.length
      });
      this.rearPrice = 0;
      this.rearSalePrice = 0;
      this.showRearSale = false;
      return;
    }

    console.log('🔍 Rear wheel data for pricing:', {
      wheelId: this.rearWheelSize,
      selectedWheel: {
        id: selectedRearWheel.id,
        sku: selectedRearWheel.sku,
        uae_retail_price: selectedRearWheel.uae_retail_price,
        price: selectedRearWheel.price,
        sale_price: selectedRearWheel.sale_price,
        us_retail_price: selectedRearWheel.us_retail_price
      },
      all_pricing_fields: Object.keys(selectedRearWheel).filter(key => key.toLowerCase().includes('price')),
      pricing_values: Object.entries(selectedRearWheel)
        .filter(([key, value]) => key.toLowerCase().includes('price'))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    });

    // Try multiple pricing fields in order of preference
    const unitPrice = selectedRearWheel.uae_retail_price || 
                     selectedRearWheel.us_retail_price || 
                     selectedRearWheel.price || 
                     0;
                     
    console.log('💵 Rear unit price calculation:', {
      uae_retail_price: selectedRearWheel.uae_retail_price,
      us_retail_price: selectedRearWheel.us_retail_price,
      price: selectedRearWheel.price,
      final_unit_price: unitPrice
    });

    this.rearPrice = unitPrice * this.rearQty;

    // Handle sale pricing for rear wheels
    if (selectedRearWheel.sale_price && selectedRearWheel.sale_price > 0) {
      this.showRearSale = true;
      this.rearSalePrice = selectedRearWheel.sale_price * this.rearQty;
    } else {
      this.showRearSale = false;
      this.rearSalePrice = 0;
    }

    console.log('💰 Rear pricing updated:', {
      wheelId: this.rearWheelSize,
      unitPrice,
      quantity: this.rearQty,
      totalPrice: this.rearPrice,
      salePrice: this.rearSalePrice,
      showSale: this.showRearSale,
      wheelSku: selectedRearWheel.sku
    });
  }

  /**
   * Calculates total price considering sale prices
   */
  private calculateTotalPrice(): void {
    let frontTotal = this.showFrontSale ? this.frontSalePrice : this.frontPrice;
    let rearTotal = this.showRearSale ? this.rearSalePrice : this.rearPrice;
    
    // Only include rear price if we have rear wheels
    if (!this.hasRearWheels) {
      rearTotal = 0;
    }
    
    this.totalPrice = frontTotal + rearTotal;
    this.onPriceChange.emit(this.totalPrice);
  }  /**
   * Updates cart items and emits changes
   */
  private updateCartItems(): void {
    if (!this.product) return;

    const items: CartItem[] = [];
    
    // Add front wheels if selected
    if (this.frontWheelSize) {
      // Find the actual wheel variant that was selected
      const frontWheel = this.availableWheels.find(w => w.id == this.frontWheelSize);
      
      if (frontWheel) {
        items.push({
          product_id: frontWheel.product_id || frontWheel.product?.id || this.product.product_id || this.product.id,
          product_variant_id: this.frontWheelSize,
          type: 'front',
          quantity: this.frontQty
        });
          console.log('✅ Added front wheel to cart items:', {
          wheel_id: this.frontWheelSize,
          product_id: frontWheel.product_id || frontWheel.product?.id,
          sku: frontWheel.sku,
          type: 'front',
          quantity: this.frontQty,
          wheel_pricing: {
            uae_retail_price: frontWheel.uae_retail_price,
            price: frontWheel.price,
            sale_price: frontWheel.sale_price,
            calculated_unit_price: frontWheel.uae_retail_price || frontWheel.price || 0
          }
        });
      }
    }
    
    // Add rear wheels if selected and available
    if (this.rearWheelSize && this.hasRearWheels) {
      // Find the actual wheel variant that was selected for rear
      const rearWheel = this.availableWheels.find(w => w.id == this.rearWheelSize);
      
      if (rearWheel) {
        items.push({
          product_id: rearWheel.product_id || rearWheel.product?.id || this.product.product_id || this.product.id,
          product_variant_id: this.rearWheelSize,
          type: 'rear',
          quantity: this.rearQty
        });
          console.log('✅ Added rear wheel to cart items:', {
          wheel_id: this.rearWheelSize,
          product_id: rearWheel.product_id || rearWheel.product?.id,
          sku: rearWheel.sku,
          type: 'rear',
          quantity: this.rearQty,
          wheel_pricing: {
            uae_retail_price: rearWheel.uae_retail_price,
            price: rearWheel.price,
            sale_price: rearWheel.sale_price,
            calculated_unit_price: rearWheel.uae_retail_price || rearWheel.price || 0
          }
        });
      }
    }
    
    console.log('🔧 Cart items updated - emitting to parent:', items);
    this.onCartItemsChange.emit(items);
  }

  /**
   * Gets the effective price for display (considering sale prices)
   */
  getEffectivePrice(type: 'front' | 'rear'): number {
    if (type === 'front') {
      return this.showFrontSale ? this.frontSalePrice : this.frontPrice;
    } else {
      return this.showRearSale ? this.rearSalePrice : this.rearPrice;
    }
  }

  /**
   * Checks if pricing is available
   */
  hasPricing(): boolean {
    return !!(this.product?.uae_retail_price || this.product?.price);
  }
}
